import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, MessageSquareOff, AlertCircle } from 'lucide-react';
import { toast } from '@/components/ui/Toast';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import SimpleSelect from '@/components/ui/SimpleSelect';
import Pagination from '@/components/ui/Pagination';
import ReviewCard from './ReviewCard';
import ReviewModal from './ReviewModal';
import ReportReviewModal from './ReportReviewModal';
import { reviewService } from '@/services/review.service';
import { ReviewType, ReviewStatus } from '@/types/review.types';
import type { Review, ReviewListParams, AdminReviewListParams } from '@/types/review.types';

interface ReviewsListProps {
  targetId: string;
  targetType: ReviewType;
  targetTitle: string;
  showWriteReview?: boolean;
  canWriteReview?: boolean;
  currentUserId?: string;
  isAdmin?: boolean;
  autoOpenReviewModal?: boolean;
}


const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: ReviewStatus.APPROVED, label: 'Approved' },
  { value: ReviewStatus.FLAGGED, label: 'Flagged' },
  { value: ReviewStatus.DELETED, label: 'Deleted' },
];

const RATING_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Ratings' },
  { value: '5', label: '5 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '2', label: '2 Stars' },
  { value: '1', label: '1 Star' },
];

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function ReviewsList({
  targetId,
  targetType,
  targetTitle,
  showWriteReview = true,
  canWriteReview = true,
  currentUserId,
  isAdmin = false,
  autoOpenReviewModal = false,
}: ReviewsListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const params: ReviewListParams = useMemo(() => ({
    skip: (currentPage - 1) * itemsPerPage,
    limit: itemsPerPage,
  }), [currentPage, itemsPerPage]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewIdToDelete, setReviewIdToDelete] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reviewIdToReport, setReviewIdToReport] = useState<string | null>(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [reviewIdToFlag, setReviewIdToFlag] = useState<string | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [reviewIdToApprove, setReviewIdToApprove] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Auto-open review modal when prop is true
  useEffect(() => {
    if (autoOpenReviewModal && showWriteReview && !isAdmin) {
      setIsReviewModalOpen(true);
    }
  }, [autoOpenReviewModal, showWriteReview, isAdmin]);

  // Helper function to get stats based on target type
  const getStats = () => {
    switch (targetType) {
      case ReviewType.COURSE:
        return reviewService.getCourseReviewStats(targetId);
      case ReviewType.CERTIFICATE:
        return reviewService.getCertificationReviewStats(targetId);
      case ReviewType.LEARNING_PATH:
        return reviewService.getLearningPathReviewStats(targetId);
      default:
        return reviewService.getCourseReviewStats(targetId);
    }
  };

  // Build params with filters for both admin and learner
  const filteredParams: AdminReviewListParams = useMemo(() => ({
    ...params,
    ...(statusFilter !== 'all' && { status: statusFilter as ReviewStatus }),
    ...(ratingFilter !== 'all' && { rating: parseInt(ratingFilter) }),
  }), [params, statusFilter, ratingFilter]);

  // Helper function to get reviews based on target type and user role
  // Admin uses separate API to see all reviews including flagged/deleted
  // Learner uses public API to see only approved reviews
  const getReviews = () => {
    if (isAdmin) {
      // Admin APIs - includes flagged and deleted reviews
      switch (targetType) {
        case ReviewType.COURSE:
          return reviewService.getAdminCourseReviews(targetId, filteredParams);
        case ReviewType.CERTIFICATE:
          return reviewService.getAdminCertificationReviews(targetId, filteredParams);
        case ReviewType.LEARNING_PATH:
          return reviewService.getAdminLearningPathReviews(targetId, filteredParams);
        default:
          return reviewService.getAdminCourseReviews(targetId, filteredParams);
      }
    } else {
      // Public APIs - only approved reviews (status filter won't have effect, rating filter works)
      switch (targetType) {
        case ReviewType.COURSE:
          return reviewService.getCourseReviews(targetId, filteredParams);
        case ReviewType.CERTIFICATE:
          return reviewService.getCertificationReviews(targetId, filteredParams);
        case ReviewType.LEARNING_PATH:
          return reviewService.getLearningPathReviews(targetId, filteredParams);
        default:
          return reviewService.getCourseReviews(targetId, filteredParams);
      }
    }
  };

  // Fetch review stats based on target type
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['reviews', targetType, targetId, 'stats'],
    queryFn: getStats,
    enabled: !!targetId,
    retry: false,
  });

  // Default stats when API fails or returns no data
  const displayStats = statsData || { avg_rating: 0, total_reviews: 0, rating_breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };

  // Query key for reviews
  const reviewsQueryKey = ['reviews', targetType, targetId, params, isAdmin ? 'admin' : 'public', statusFilter, ratingFilter];

  // Fetch reviews based on target type and user role
  // Admin API returns all reviews (including flagged/deleted)
  // Learner API returns only approved reviews
  const { data: reviewsData, isLoading: reviewsLoading, refetch, isError: reviewsError } = useQuery({
    queryKey: reviewsQueryKey,
    queryFn: getReviews,
    enabled: !!targetId,
    retry: false,
    // Return empty reviews on error
    placeholderData: { reviews: [], total_count: 0 },
  });

  // Client-side sorting by most recent
  const sortedReviews = useMemo(() => {
    const reviews = reviewsData?.reviews || [];
    return [...reviews].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [reviewsData?.reviews]);

  // Fetch current user's own reviews (includes flagged reviews)
  // This helps detect if user has a review that's not visible in the main list
  const { data: myReviewsData } = useQuery({
    queryKey: ['my-reviews', currentUserId],
    queryFn: () => reviewService.getMyReviews(),
    enabled: !!currentUserId && !isAdmin && showWriteReview,
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Find user's review for this specific target (even if flagged)
  const userReviewForTarget = useMemo(() => {
    if (!myReviewsData?.reviews || !currentUserId) return undefined;

    return myReviewsData.reviews.find((review) => {
      if (targetType === ReviewType.COURSE && review.course_id === targetId) return true;
      if (targetType === ReviewType.CERTIFICATE && review.certification_id === targetId) return true;
      if (targetType === ReviewType.LEARNING_PATH && review.learning_path_id === targetId) return true;
      return false;
    });
  }, [myReviewsData?.reviews, currentUserId, targetId, targetType]);

  // Mutations
  const markHelpful = useMutation({
    mutationFn: (reviewId: string) => reviewService.markHelpful(reviewId),
    onSuccess: (data) => {
      // Update the review in cache with new helpful_count and has_marked_helpful
      queryClient.setQueryData(reviewsQueryKey, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          reviews: oldData.reviews.map((review: Review) =>
            review._id === data.review_id
              ? {
                  ...review,
                  helpful_count: data.helpful_count,
                  has_marked_helpful: data.is_helpful,
                }
              : review
          ),
        };
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to mark as helpful');
    },
  });

  const deleteReview = useMutation({
    mutationFn: (reviewId: string) => reviewService.deleteReview(reviewId),
    onSuccess: () => {
      toast.success('Review deleted');
      refetch();
      refetchStats(); // Also refresh stats when review is deleted
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete review');
    },
  });

  // Admin mutations
  const adminDeleteReview = useMutation({
    mutationFn: (reviewId: string) => reviewService.adminDeleteReview(reviewId),
    onSuccess: () => {
      toast.success('Review deleted');
      refetch();
      refetchStats(); // Also refresh stats when review is deleted
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete review');
    },
  });

  const updateReviewStatus = useMutation({
    mutationFn: ({ reviewId, status }: { reviewId: string; status: ReviewStatus }) =>
      reviewService.updateReviewStatus(reviewId, { status }),
    onSuccess: (_, variables) => {
      const statusMessages: Record<ReviewStatus, string> = {
        [ReviewStatus.APPROVED]: 'Review approved',
        [ReviewStatus.FLAGGED]: 'Review flagged',
        [ReviewStatus.DELETED]: 'Review deleted',
      };
      toast.success(statusMessages[variables.status] || 'Status updated');
      refetch();
      refetchStats(); // Also refresh stats when status changes
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update status');
    },
  });

  const handleStatusFilterChange = (value: string | number | null) => {
    setStatusFilter(value as string || 'all');
    setCurrentPage(1);
  };

  const handleRatingFilterChange = (value: string | number | null) => {
    setRatingFilter(value as string || 'all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  };

  const handleEdit = (review: Review) => {
    setEditingReview(review);
    setIsReviewModalOpen(true);
  };

  const handleDeleteClick = (reviewId: string) => {
    setReviewIdToDelete(reviewId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (reviewIdToDelete) {
      if (isAdmin) {
        await adminDeleteReview.mutateAsync(reviewIdToDelete);
      } else {
        await deleteReview.mutateAsync(reviewIdToDelete);
      }
      setShowDeleteModal(false);
      setReviewIdToDelete(null);
    }
  };

  const handleFlagClick = (reviewId: string) => {
    setReviewIdToFlag(reviewId);
    setShowFlagModal(true);
  };

  const handleApproveClick = (reviewId: string) => {
    setReviewIdToApprove(reviewId);
    setShowApproveModal(true);
  };

  const handleFlagConfirm = async () => {
    if (reviewIdToFlag) {
      await updateReviewStatus.mutateAsync({ reviewId: reviewIdToFlag, status: ReviewStatus.FLAGGED });
      setShowFlagModal(false);
      setReviewIdToFlag(null);
    }
  };

  const handleApproveConfirm = async () => {
    if (reviewIdToApprove) {
      await updateReviewStatus.mutateAsync({ reviewId: reviewIdToApprove, status: ReviewStatus.APPROVED });
      setShowApproveModal(false);
      setReviewIdToApprove(null);
    }
  };

  const handleUpdateStatus = async (reviewId: string, status: ReviewStatus) => {
    // Use modals for confirmation instead of direct action
    if (status === ReviewStatus.FLAGGED) {
      handleFlagClick(reviewId);
    } else if (status === ReviewStatus.APPROVED) {
      handleApproveClick(reviewId);
    }
  };

  const handleReportClick = (reviewId: string) => {
    setReviewIdToReport(reviewId);
    setShowReportModal(true);
  };

  const handleHelpful = async (reviewId: string) => {
    await markHelpful.mutateAsync(reviewId);
  };

  const handleWriteReview = () => {
    // If user already has a review, open in edit mode
    if (userExistingReview) {
      setEditingReview(userExistingReview);
    } else {
      setEditingReview(undefined);
    }
    setIsReviewModalOpen(true);
  };

  const handleModalClose = () => {
    setIsReviewModalOpen(false);
    setEditingReview(undefined);
  };

  const handleReviewSuccess = () => {
    refetch();
  };

  const reviews = sortedReviews;
  const totalCount = reviewsData?.total_count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Step back a page if the current one no longer exists (e.g. last review on the page was deleted)
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const isLoading = statsLoading || reviewsLoading;

  // Check if current user already has a review for this target
  // First check visible reviews, then check user's own reviews (which includes flagged)
  const userVisibleReview = currentUserId
    ? reviews.find((r) => r.user_id === currentUserId)
    : undefined;

  // Use visible review if available, otherwise use the one from my-reviews (may be flagged)
  const userExistingReview = userVisibleReview || userReviewForTarget;

  // Check if user has a flagged review (not visible but exists)
  const userHasFlaggedReview = !userVisibleReview && userReviewForTarget &&
    userReviewForTarget.status === ReviewStatus.FLAGGED;

  // Format large numbers (e.g., 15000 -> "15K")
  const formatCount = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  // Calculate rating percentage for breakdown
  const getRatingPercentage = (starCount: number): number => {
    if (!displayStats || displayStats.total_reviews === 0) return 0;
    const count = displayStats.rating_breakdown[starCount as keyof typeof displayStats.rating_breakdown] || 0;
    return Math.round((count / displayStats.total_reviews) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Rating Header with Breakdown */}
      {!statsLoading && (
        <div className="bg-gray-50 rounded-xl p-5">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            {/* Left: Overall Rating */}
            <div className="flex flex-col items-center lg:items-start lg:min-w-[140px]">
              <div className="text-4xl font-bold text-gray-900 mb-1">
                {displayStats.avg_rating.toFixed(1)}
              </div>
              <div className="flex items-center gap-0.5 mb-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < Math.floor(displayStats.avg_rating) ? 'fill-yellow-400 text-yellow-400' : i < displayStats.avg_rating ? 'fill-yellow-400/50 text-yellow-400' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500">
                {formatCount(displayStats.total_reviews)} {displayStats.total_reviews === 1 ? 'rating' : 'ratings'}
              </span>
            </div>

            {/* Middle: Rating Breakdown */}
            <div className="flex-1">
              <div className="space-y-1.5">
                {[5, 4, 3, 2, 1].map((star) => {
                  const percentage = getRatingPercentage(star);
                  const count = displayStats.rating_breakdown[star as keyof typeof displayStats.rating_breakdown] || 0;
                  return (
                    <div key={star} className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5 w-16 justify-end">
                        <span className="text-xs text-gray-600 w-3">{star}</span>
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      </div>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-[200px]">
                        <div
                          className="h-full bg-yellow-400 rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-20 whitespace-nowrap">{percentage}% ({count})</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Write Review Button - Only show when there are reviews and user hasn't written one yet */}
            {showWriteReview && !isAdmin && reviews.length > 0 && !userExistingReview && (
              <div className="flex lg:flex-col items-center lg:items-end gap-2">
                <button
                  onClick={() => canWriteReview && handleWriteReview()}
                  disabled={!canWriteReview}
                  title={!canWriteReview ? 'You do not have permission to write a review' : undefined}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                    canWriteReview
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-primary-600 text-white opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Star size={16} />
                  Write a Review
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters - Right aligned */}
      <div className="flex flex-wrap items-center gap-3 justify-end">
        {/* Status filter - Only for admin (learners only see approved reviews) */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Status:</span>
            <div className="w-36">
              <SimpleSelect
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={STATUS_FILTER_OPTIONS}
                isSearchable={false}
              />
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Rating:</span>
          <div className="w-32">
            <SimpleSelect
              value={ratingFilter}
              onChange={handleRatingFilterChange}
              options={RATING_FILTER_OPTIONS}
              isSearchable={false}
            />
          </div>
        </div>
      </div>

      {/* Info banner when user's review is flagged */}
      {userHasFlaggedReview && !isAdmin && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Your review is currently under moderation and will be visible once approved.
          </p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-24" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-4/5" />
                <div className="h-3 bg-gray-200 rounded w-3/5" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reviews List - Single Column */}
      {!isLoading && reviews.length > 0 && (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review._id}
              review={review}
              isOwner={!isAdmin && currentUserId === review.user_id}
              isAdmin={isAdmin}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onHelpful={handleHelpful}
              onReport={handleReportClick}
              onUpdateStatus={handleUpdateStatus}
            />
          ))}
        </div>
      )}

      {/* Empty State - Also shown when API error (e.g., 404) */}
      {!isLoading && (reviews.length === 0 || reviewsError) && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquareOff size={28} className="text-gray-400" />
          </div>
          {/* Check if filters are applied */}
          {ratingFilter !== 'all' || statusFilter !== 'all' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews found</h3>
              <p className="text-sm text-gray-500">
                No reviews match the selected filters. Try adjusting your filters.
              </p>
            </>
          ) : userHasFlaggedReview ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Your review is under moderation</h3>
              <p className="text-sm text-gray-500">
                Your review has been flagged and is currently being reviewed by our team.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
              <p className="text-sm text-gray-500 mb-6">
                {isAdmin
                  ? 'No reviews have been submitted for this course yet.'
                  : 'Be the first to share your experience with this course.'
                }
              </p>
              {showWriteReview && !isAdmin && !userExistingReview && (
                <button
                  onClick={() => canWriteReview && handleWriteReview()}
                  disabled={!canWriteReview}
                  title={!canWriteReview ? 'You do not have permission to write a review' : undefined}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                    canWriteReview
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-primary-600 text-white opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Star size={16} />
                  Write a Review
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !reviewsError && totalPages > 1 && (
        <div className="rounded-xl border border-gray-100 overflow-hidden">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={handleModalClose}
        targetId={targetId}
        targetTitle={targetTitle}
        targetType={targetType}
        existingReview={editingReview}
        onSuccess={handleReviewSuccess}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setReviewIdToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Review"
        message="Are you sure you want to delete this review?"
        isLoading={isAdmin ? adminDeleteReview.isPending : deleteReview.isPending}
      />

      {/* Report Review Modal */}
      {reviewIdToReport && (
        <ReportReviewModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setReviewIdToReport(null);
          }}
          reviewId={reviewIdToReport}
          onSuccess={() => refetch()}
        />
      )}

      {/* Flag Review Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showFlagModal}
        onClose={() => {
          setShowFlagModal(false);
          setReviewIdToFlag(null);
        }}
        onConfirm={handleFlagConfirm}
        title="Flag Review"
        message="Are you sure you want to flag this review? Flagged reviews will be hidden from learners."
        isLoading={updateReviewStatus.isPending}
        variant="warning"
        confirmButtonText="Flag"
      />

      {/* Approve Review Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setReviewIdToApprove(null);
        }}
        onConfirm={handleApproveConfirm}
        title="Approve Review"
        message="Are you sure you want to approve this review? Approved reviews will be visible to all learners."
        isLoading={updateReviewStatus.isPending}
        variant="success"
        confirmButtonText="Approve"
      />
    </div>
  );
}
